<template>
    <div class="bg-black rounded-lg shadow-md text-white p-6">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div v-if="isLoading" class="col-span-2 md:col-span-4 flex items-center justify-center py-8">
            <div class="text-center">
                <i class="mdi mdi-loading mdi-spin text-2xl text-primary mb-2"></i>
                <p class="text-neutral-400 text-sm">Loading stats...</p>
            </div>
        </div>

        <div v-else-if="hasError" class="col-span-2 md:col-span-4 flex items-center justify-center py-8">
            <div class="text-center">
                <i class="mdi mdi-alert-circle text-2xl text-red-400 mb-2"></i>
                <p class="text-neutral-400 text-sm">Unable to load stats</p>
            </div>
        </div>

        <template v-else>
            <!-- Coral Cover - Main Metric -->
            <div class="flex flex-col bg-neutral-800 p-4 rounded-lg cursor-help" 
                 title="Percentage of total reef area covered by live coral. Calculated as: Live Coral Area ÷ Total Surface Area × 100">
                <span class="text-2xl md:text-3xl font-black font-lato text-white">
                    {{ stats.percentCoralCover }}%
                </span>
                <span class="text-xs text-neutral-400 uppercase">Coral Cover</span>
            </div>

            <!-- Health Ratio -->
            <div class="flex flex-col bg-neutral-800 p-4 rounded-lg cursor-help"
                 title="Percentage of coral that is alive vs dead/bleached. Calculated as: Live Coral Area ÷ (Live Coral Area + Dead/Bleached Coral Area) × 100">
                <span class="text-2xl md:text-3xl font-black font-lato text-white">
                    {{ healthRatio }}%
                </span>
                <span class="text-xs text-neutral-400 uppercase">Coral Health</span>
            </div>

            <!-- Coral Bleaching -->
            <div class="flex flex-col bg-neutral-800 p-4 rounded-lg cursor-help"
                 title="Percentage of total reef area that is bleached coral. Calculated as: Total Bleached Coral Area ÷ Total Surface Area × 100">
                <span class="text-2xl md:text-3xl font-black font-lato text-white">
                    {{ coralBleachingPercent }}%
                </span>
                <span class="text-xs text-neutral-400 uppercase">Coral Bleaching</span>
            </div>

            <!-- Algae Cover -->
            <div class="flex flex-col bg-neutral-800 p-4 rounded-lg cursor-help"
                 title="Percentage of total reef area covered by algae. Calculated as: Algae Covered Substrate Area ÷ Total Surface Area × 100">
                <span class="text-2xl md:text-3xl font-black font-lato text-white">
                    {{ stats.algaeCover.percent }}%
                </span>
                <span class="text-xs text-neutral-400 uppercase">Algae Cover</span>
            </div>
        </template>
        </div>
    </div>
</template>

<script>
import { storage_resto } from '../main';
import { ref as storageRef, getDownloadURL } from 'firebase/storage';

export default {
    name: 'SurveyStatsHeader',
    props: {
        dataPath: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            rawData: {},
            isLoading: true,
            hasError: false
        };
    },
    mounted() {
        this.loadStatistics();
    },
    computed: {
        stats() {
            if (!this.rawData || Object.keys(this.rawData).length === 0) {
                return this.getEmptyStats();
            }

            // Parse basic metrics
            const totalVertices = parseInt(this.rawData.total_vertices) || 0;
            const totalSurfaceArea = parseFloat(this.rawData.total_surface_area) || 0;
            const liveCoralArea = parseFloat(this.rawData.total_live_coral_area) || 0;
            
            // Calculate percent coral cover if not provided
            const percentCoralCover = totalSurfaceArea > 0 
                ? parseFloat(((liveCoralArea / totalSurfaceArea) * 100).toFixed(1))
                : 0;
            
            const liveCoral = {
                area: liveCoralArea,
                percent: percentCoralCover
            };
            const suitableSubstrate = parseFloat(this.rawData.suitable_substrate_area) || 0;

            // Calculate algae cover
            const algaeCover = {
                area: parseFloat(this.rawData.class_algae_covered_substrate_area) || 0,
                percent: parseFloat((parseFloat(this.rawData.class_algae_covered_substrate_area_percent) || 0).toFixed(1))
            };

            // Calculate dead coral totals
            const deadCoralArea = this.calculateDeadCoralArea();
            const deadCoral = {
                area: deadCoralArea,
                percent: totalSurfaceArea > 0 ? parseFloat(((deadCoralArea / totalSurfaceArea) * 100).toFixed(1)) : 0
            };

            return {
                totalVertices,
                totalSurfaceArea,
                percentCoralCover,
                liveCoral,
                deadCoral,
                algaeCover,
                suitableSubstrate
            };
        },

        healthRatio() {
            const liveArea = this.stats.liveCoral.area;
            const deadArea = this.stats.deadCoral.area;
            const totalCoralArea = liveArea + deadArea;
            
            if (totalCoralArea === 0) return 0;
            return ((liveArea / totalCoralArea) * 100).toFixed(1);
        },

        coralBleachingPercent() {
            const bleachedTypes = [
                'massive_meandering_bleached',
                'other_coral_bleached',
                'branching_bleached',
                'meandering_bleached'
            ];

            const totalBleachedArea = bleachedTypes.reduce((total, type) => {
                return total + (parseFloat(this.rawData[`class_${type}_area`]) || 0);
            }, 0);

            const totalSurfaceArea = parseFloat(this.rawData.total_surface_area) || 0;
            
            if (totalSurfaceArea === 0) return 0;
            return ((totalBleachedArea / totalSurfaceArea) * 100).toFixed(1);
        }
    },
    methods: {
        async loadStatistics() {
            try {
                this.isLoading = true;
                this.hasError = false;

                if (!this.dataPath) {
                    throw new Error('No dataPath provided');
                }

                // Get the download URL for parsed.txt from Firebase Storage
                const storagePath = `${this.dataPath}/output/parsed.txt`;
                console.log('Loading statistics from:', storagePath);
                
                const fileRef = storageRef(storage_resto, storagePath);
                const downloadUrl = await getDownloadURL(fileRef);
                
                const response = await fetch(downloadUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const text = await response.text();
                this.parseStatisticsData(text);
                
            } catch (error) {
                console.error('Error loading statistics:', error);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        parseStatisticsData(text) {
            const lines = text.trim().split('\n');
            const data = {};
            
            lines.forEach(line => {
                if (line.trim()) {
                    const [key, value] = line.split(',');
                    data[key] = value;
                }
            });
            
            this.rawData = data;
        },

        calculateDeadCoralArea() {
            const deadTypes = [
                'massive_meandering_dead',
                'massive_meandering_bleached',
                'other_coral_dead',
                'other_coral_bleached',
                'branching_dead',
                'branching_bleached',
                'meandering_dead',
                'meandering_bleached'
            ];

            return deadTypes.reduce((total, type) => {
                return total + (parseFloat(this.rawData[`class_${type}_area`]) || 0);
            }, 0);
        },

        getEmptyStats() {
            return {
                totalVertices: 0,
                totalSurfaceArea: 0,
                percentCoralCover: 0,
                liveCoral: { area: 0, percent: 0 },
                deadCoral: { area: 0, percent: 0 },
                algaeCover: { area: 0, percent: 0 },
                suitableSubstrate: 0
            };
        }
    },
    watch: {
        dataPath: {
            handler() {
                // When dataPath changes, reload statistics
                this.loadStatistics();
            },
            immediate: false
        }
    }
};
</script>

<style scoped>
.text-primary {
    color: #27bdf4;
}
</style>
